/**
 * @fileoverview Text-to-Speech API client for Amazon Polly integration
 *
 * Provides methods for synthesizing speech using Amazon Polly
 * with streaming audio responses.
 */

import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Synthesize speech from text using Amazon Polly
 * @param {string} text - Text to convert to speech
 * @param {Object} options - TTS options
 * @param {string} options.voice - Voice ID (e.g., 'joanna', 'matthew')
 * @param {string} options.speed - Speech speed ('x-slow', 'slow', 'medium', 'fast', 'x-fast')
 * @param {string} options.format - Audio format ('mp3', 'ogg_vorbis', 'pcm')
 * @returns {Promise<Blob>} Audio blob
 */
export async function synthesizeSpeech(text, options = {}) {
  const {
    voice = 'joanna',
    speed = 'medium',
    format = 'mp3'
  } = options;

  try {
    const response = await axios.post(`${API_BASE_URL}/tts/synthesize`, {
      text,
      voice,
      speed,
      format
    }, {
      responseType: 'blob',
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 30000 // 30 second timeout
    });

    return response.data;
  } catch (error) {

    if (error.response?.data) {
      // Try to parse error response
      try {
        const errorText = await error.response.data.text();
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'TTS synthesis failed');
      } catch (parseError) {
        throw new Error('TTS synthesis failed');
      }
    }

    throw new Error(error.message || 'TTS synthesis failed');
  }
}

/**
 * Get available Polly voices
 * @returns {Promise<Object>} Available voices and default voice
 */
export async function getAvailableVoices() {
  try {
    const response = await axios.get(`${API_BASE_URL}/tts/voices`);
    return response.data;
  } catch (error) {
    throw new Error('Failed to get available voices');
  }
}

/**
 * Check TTS service health
 * @returns {Promise<Object>} Service health status
 */
export async function getTTSHealth() {
  try {
    const response = await axios.get(`${API_BASE_URL}/tts/health`);
    return response.data;
  } catch (error) {
    throw new Error('TTS service unavailable');
  }
}

/**
 * Play audio blob through the browser's audio API
 * @param {Blob} audioBlob - Audio data to play
 * @param {Object} options - Playback options
 * @param {number} options.volume - Volume (0.0 to 1.0)
 * @param {number} options.playbackRate - Playback rate (0.25 to 4.0)
 * @returns {Promise<HTMLAudioElement>} Audio element for control
 */
export async function playAudioBlob(audioBlob, options = {}) {
  const {
    volume = 1.0,
    playbackRate = 1.0
  } = options;

  return new Promise((resolve, reject) => {
    try {
      // Create audio element
      const audio = new Audio();

      // Create object URL from blob
      const audioUrl = URL.createObjectURL(audioBlob);

      // Set up audio element
      audio.src = audioUrl;
      audio.volume = Math.max(0, Math.min(1, volume));
      audio.playbackRate = Math.max(0.25, Math.min(4, playbackRate));

      // Clean up object URL when audio ends or errors
      const cleanup = () => {
        URL.revokeObjectURL(audioUrl);
      };

      // Set up event handlers
      audio.onended = () => {
        cleanup();
      };

      audio.onerror = () => {
        cleanup();
        reject(new Error('Audio playback failed'));
      };

      audio.oncanplay = () => {
        audio.play()
          .then(() => resolve(audio))
          .catch(reject);
      };

      // Load the audio
      audio.load();
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Convenience function to synthesize and play speech in one call
 * @param {string} text - Text to speak
 * @param {Object} options - Combined TTS and playback options
 * @returns {Promise<HTMLAudioElement>} Audio element for control
 */
export async function speakText(text, options = {}) {
  const {
    voice,
    speed,
    format,
    volume,
    playbackRate,
    ...ttsOptions
  } = options;

  try {
    // Synthesize speech
    const audioBlob = await synthesizeSpeech(text, {
      voice,
      speed,
      format,
      ...ttsOptions
    });

    // Play audio
    const audioElement = await playAudioBlob(audioBlob, {
      volume,
      playbackRate
    });

    return audioElement;
  } catch (error) {
    throw error;
  }
}

export default {
  synthesizeSpeech,
  getAvailableVoices,
  getTTSHealth,
  playAudioBlob,
  speakText
};